import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import {
  adminProUsers,
  adminProCreators,
  adminProEarnings,
  adminProPayouts,
  adminProTransactions,
  adminCloseMonth,
  adminSetPayoutStatus,
  adminRefundEarning,
  formatDate,
  formatMoney,
  formatMonth,
  formatRemaining,
} from "../../services/proSystemApi";
import "./AdminPro.css";
import "./Pro.css";

// =====================================================
// ADMIN — PRO SYSTEM  (Steps 23, 24, 25, 26, 27)
//
// The five sections Step 26 lists, as five tabs:
//   Pro Users · Pro Creators · Payments & Earnings · Payouts · Transactions
//
// Plus the three things an admin actually DOES:
//   close a month (Step 23), move a payout along (Step 24), record a refund
//   (Step 25).
//
// THE PAGE COMPUTES NOTHING. Every rupee figure, every status and every total
// is sent by the server already worked out. An admin screen that did its own
// arithmetic would be a second, unreviewed implementation of the ledger.
//
// Step 27's four bank columns — Bank Added, Email Verified, Payout Amount,
// Payout Status — live in the Pro Creators tab, next to the creator they
// describe, so "can this person be paid, and what is outstanding" is
// answerable without opening anything.
// =====================================================

const TABS = [
  { key: "users", label: "Pro Users", icon: "👤" },
  { key: "creators", label: "Pro Creators", icon: "💎" },
  { key: "earnings", label: "Payments & Earnings", icon: "💰" },
  { key: "payouts", label: "Payouts", icon: "🏦" },
  { key: "transactions", label: "Transactions", icon: "🧾" },
];

const PAYOUT_STATUSES = ["pending", "processing", "paid", "failed", "on_hold"];

const STATUS_LABEL = {
  pending: "Pending",
  processing: "Processing",
  paid: "Paid",
  failed: "Failed",
  on_hold: "On Hold",
};

const LOADERS = {
  users: adminProUsers,
  creators: adminProCreators,
  earnings: adminProEarnings,
  payouts: () => adminProPayouts(),
  transactions: adminProTransactions,
};

function AdminPro() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("users");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const flash = useCallback((msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const load = useCallback(
    async (which) => {
      try {
        setLoading(true);
        const res = await LOADERS[which]();
        setData((prev) => ({ ...prev, [which]: res }));
        setError(null);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load(tab);
  }, [load, tab]);

  // ── CLOSE A MONTH (Step 23) ──
  // Defaults to the previous month — the one the 1st–8th window pays for. The
  // server refuses a month that has not ended, so there is nothing to guard
  // here beyond asking first.
  const closeMonth = async () => {
    const payable = data.payouts?.window?.payableMonth;

    const confirmed = window.confirm(
      `Close ${formatMonth(payable)} and raise payouts for every creator with earnings in it?\n\n` +
        "This is safe to run more than once — a month that is already closed will not be closed again."
    );

    if (!confirmed) return;

    setBusy(true);

    try {
      const res = await adminCloseMonth(payable);

      // "0 raised" reads like a failure when it is usually the correct answer:
      // nobody earned anything that month. Say which it is, rather than making
      // the admin guess from a bare zero.
      if (res.created === 0) {
        flash(
          `${formatMonth(payable)} closed — no creator earned anything that month, so there is nothing to pay.`,
          "warn"
        );
      } else {
        flash(res.message, "success");
      }

      await load("payouts");
    } catch (err) {
      flash(err?.response?.data?.message || "Failed to close the month", "error");
    } finally {
      setBusy(false);
    }
  };

  // ── MOVE A PAYOUT ALONG (Step 24) ──
  const setPayoutStatus = async (payout, status) => {
    let reference = "";

    if (status === "paid") {
      reference = window.prompt("Bank reference / UTR for this transfer (optional):", "") || "";
    }

    let note = "";

    if (status === "failed" || status === "on_hold") {
      note = window.prompt("Reason (shown to the creator):", "") || "";
    }

    setBusy(true);

    try {
      const res = await adminSetPayoutStatus(payout._id, { status, reference, note });
      flash(res.message, "success");
      await load("payouts");
    } catch (err) {
      flash(err?.response?.data?.message || "Failed to update the payout", "error");
    } finally {
      setBusy(false);
    }
  };

  // ── REFUND (Step 25) ──
  // This really sends the money back through Razorpay — the admin does not
  // have to open the Razorpay dashboard. If Razorpay refuses (for instance
  // because the payment was already refunded there), nothing is written to the
  // books and the admin is offered the "record only" fallback.
  const refund = async (row, force = false) => {
    if (!force) {
      const ok = window.confirm(
        `Refund ${formatMoney(row.grossRupees)} to ${row.user?.name || "this reader"}?\n\n` +
          "This sends the money back through Razorpay immediately.\n" +
          `${row.creator?.name || "The creator"}'s balance drops by ` +
          `${formatMoney(row.netRupees)} and the reader loses access straight away.\n\n` +
          "This cannot be undone."
      );
      if (!ok) return;
    }

    const reason = window.prompt("Reason (optional, shown in the records):", "") ?? "";

    setBusy(true);

    try {
      const res = await adminRefundEarning(row._id, reason, force);
      flash(res.message, res.settledPayout ? "warn" : "success");
      await load("transactions");
    } catch (err) {
      const data = err?.response?.data;

      // Razorpay refused. Offer the books-only correction rather than leaving
      // the admin stuck — but make them choose it explicitly.
      if (data?.reason === "razorpay_refund_failed") {
        const recordAnyway = window.confirm(
          `${data.message}\n\n` +
            "Nothing has been changed.\n\n" +
            "If this payment was ALREADY refunded in the Razorpay dashboard, press OK " +
            "to correct the books only (no money will move).\n\n" +
            "Otherwise press Cancel."
        );

        setBusy(false);
        if (recordAnyway) await refund(row, true);
        return;
      }

      flash(data?.message || "Failed to refund", "error");
    } finally {
      setBusy(false);
    }
  };

  // ── REVEAL FULL BANK DETAILS  (Step 27) ──
  //
  // The full account number and IFSC are sent with BOTH the creators list and
  // the payouts list, but are never printed into a table — Step 27 says not to
  // expose bank data unnecessarily, and a table full of account numbers is
  // exactly that. They are shown only when an admin asks for a specific one.
  //
  // This is deliberately reachable from TWO places. Payouts only exist after a
  // month has been closed, so a reveal that lived only there would be
  // unreachable for most of the month — which is precisely what happened. The
  // Pro Creators tab always has every creator, so the details are always
  // available from there.
  const revealBank = (bank, heading, creatorName) => {
    if (!bank) {
      window.alert(`${creatorName || "This creator"} has not added bank details yet.`);
      return;
    }

    window.prompt(
      `${heading}\n\n` +
        `Account holder : ${bank.accountHolderName}\n` +
        `Bank           : ${bank.bankName}\n` +
        `Account number : ${bank.accountNumber}\n` +
        `IFSC           : ${bank.ifsc}\n` +
        `Contact        : ${bank.phone || "—"}\n` +
        `Email verified : ${bank.emailVerified ? "YES" : "NO"}\n\n` +
        "Account number (copy this):",
      bank.accountNumber
    );
  };

  // From the Payouts tab — carries the amount and the month being paid.
  const showPayoutBank = (p) =>
    revealBank(
      p.payTo,
      `Transfer ${formatMoney(p.amountRupees)} to ${p.creator?.name || ""}\n${formatMonth(p.monthKey)}`,
      p.creator?.name
    );

  // From the Pro Creators tab — available at any time, payout or not.
  const showCreatorBank = (row) =>
    revealBank(row.bank, `${row.creator?.name || "Creator"} — bank details`, row.creator?.name);

  const current = data[tab];

  return (
    <div className="pro-page-wrap">
      <Navbar />

      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="pro-container apro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🛠 ADMIN</div>
          <h1 className="pro-hero-title">Pro System</h1>
          <p className="pro-hero-sub">Manage users, creators, earnings and payouts</p>
        </header>

        <div className="pro-tabs pro-tabs-scroll" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`pro-tab${tab === t.key ? " pro-tab-active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              <span aria-hidden="true">{t.icon}</span> {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={() => load(tab)}>
              Try again
            </button>
          </div>
        )}

        {/* ── 1. PRO USERS ── */}
        {!loading && !error && tab === "users" && current && (
          <section>
            <p className="apro-summary">
              {current.activeCount} active of {current.totalCount} access records
            </p>

            <div className="pro-table-scroll">
              <table className="pro-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Creator</th>
                    <th>Paid</th>
                    <th>Payment Date</th>
                    <th>Expiry</th>
                    <th>Remaining</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {current.items.map((row) => (
                    <tr key={row._id}>
                      <td>
                        <span className="pro-table-strong">{row.user?.name || "—"}</span>
                        <small className="pro-table-sub">{row.user?.email || ""}</small>
                      </td>
                      <td>{row.creator?.name || "—"}</td>
                      <td>{formatMoney(row.amountRupees)}</td>
                      <td>{formatDate(row.paymentDate)}</td>
                      <td>{formatDate(row.expiryDate)}</td>
                      <td>{row.status === "active" ? formatRemaining(row.remainingMs) : "—"}</td>
                      <td>
                        <span className={`pro-status-pill pro-status-${row.status}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── 2. PRO CREATORS  +  STEP 27 BANK STATUS ── */}
        {!loading && !error && tab === "creators" && current && (
          <section>
            <div className="pro-table-scroll">
              <table className="pro-table">
                <thead>
                  <tr>
                    <th>Creator</th>
                    <th>Blogs</th>
                    <th>Paid Users</th>
                    <th>Total Earned</th>
                    <th>This Month</th>
                    <th>Bank Added</th>
                    <th>Email Verified</th>
                    <th>Payout Amount</th>
                    <th>Payout Status</th>
                  </tr>
                </thead>
                <tbody>
                  {current.items.map((row) => (
                    <tr key={row.creator._id}>
                      <td>
                        <span className="pro-table-strong">{row.creator.name}</span>
                        <small className="pro-table-sub">{row.creator.email}</small>
                      </td>
                      <td>{row.blogCount}</td>
                      <td>{row.paidUserCount}</td>
                      <td>{formatMoney(row.totalEarningsRupees)}</td>
                      <td>{formatMoney(row.thisMonthRupees)}</td>

                      {/* ── STEP 27 ── */}
                      <td>
                        <span className={`apro-flag apro-flag-${row.bankAdded ? "yes" : "no"}`}>
                          {row.bankAdded ? "Yes" : "No"}
                        </span>
                        {row.bank?.accountLast4 && (
                          <>
                            <small className="pro-table-sub">
                              {row.bank.bankName} ••••{row.bank.accountLast4}
                            </small>
                            {/* The full account number + IFSC, on request.
                                Available here at ALL times — the Payouts tab
                                only has rows once a month has been closed. */}
                            <button
                              type="button"
                              className="pro-btn pro-btn-ghost pro-btn-sm"
                              style={{ marginTop: 6 }}
                              onClick={() => showCreatorBank(row)}
                            >
                              🏦 View full details
                            </button>
                          </>
                        )}
                      </td>
                      <td>
                        <span
                          className={`apro-flag apro-flag-${row.emailVerified ? "yes" : "no"}`}
                        >
                          {row.emailVerified ? "Verified" : "Not verified"}
                        </span>
                      </td>
                      <td className="pro-table-strong">
                        {formatMoney(row.payoutAmountRupees)}
                      </td>
                      <td>
                        {row.payoutStatus === "none" ? (
                          <span className="pro-table-sub">—</span>
                        ) : (
                          <>
                            <span className={`pro-status-pill pro-status-${row.payoutStatus}`}>
                              {STATUS_LABEL[row.payoutStatus] || row.payoutStatus}
                            </span>
                            <small className="pro-table-sub">
                              {formatMonth(row.payoutMonth)}
                            </small>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── 3. PAYMENTS & EARNINGS ── */}
        {!loading && !error && tab === "earnings" && current && (
          <section>
            <div className="pro-money-grid">
              <div className="pro-money-card pro-money-primary">
                <small>Gross collected</small>
                <strong>{formatMoney(current.totals.grossRupees)}</strong>
                <span className="pro-money-sub">{current.totals.payments} payments</span>
              </div>
              <div className="pro-money-card">
                <small>This month payments</small>
                <strong>{formatMoney(current.thisMonth.grossRupees)}</strong>
                <span className="pro-money-sub">
                  {current.thisMonth.payments} payment
                  {current.thisMonth.payments === 1 ? "" : "s"} ·{" "}
                  {formatMonth(current.thisMonth.monthKey)}
                </span>
              </div>
              <div className="pro-money-card">
                <small>Platform commission ({current.commissionPercent}%)</small>
                <strong>{formatMoney(current.totals.platformCommissionRupees)}</strong>
              </div>
              <div className="pro-money-card">
                <small>Creator earnings</small>
                <strong>{formatMoney(current.totals.creatorEarningsRupees)}</strong>
              </div>
              <div className="pro-money-card">
                <small>Refunded</small>
                <strong>{formatMoney(current.refunds.grossRupees)}</strong>
                <span className="pro-money-sub">{current.refunds.count} payments</span>
              </div>
            </div>

            <div className="pro-section-head">
              <h2>📅 By month</h2>
            </div>

            <div className="pro-table-scroll">
              <table className="pro-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Payments</th>
                    <th>Gross</th>
                    <th>Commission</th>
                    <th>Creator earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {current.months.map((m) => (
                    <tr key={m.monthKey}>
                      <td>{formatMonth(m.monthKey)}</td>
                      <td>{m.payments}</td>
                      <td>{formatMoney(m.grossRupees)}</td>
                      <td>{formatMoney(m.platformCommissionRupees)}</td>
                      <td className="pro-table-strong">
                        {formatMoney(m.creatorEarningsRupees)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ── 4. PAYOUTS  (Steps 23, 24) ── */}
        {!loading && !error && tab === "payouts" && current && (
          <section>
            <div className={`pro-card pro-notice${current.window.openNow ? "" : " pro-notice-warn"}`}>
              <p>
                The payout window runs from the <strong>{current.window.startDay}st</strong>{" "}
                to the <strong>{current.window.endDay}th</strong> of each month and is
                currently <strong>{current.window.openNow ? "OPEN" : "closed"}</strong>. The
                month it pays for is <strong>{formatMonth(current.window.payableMonth)}</strong>.
              </p>
              <button
                type="button"
                className="pro-btn pro-btn-primary"
                onClick={closeMonth}
                disabled={busy}
              >
                Close {formatMonth(current.window.payableMonth)} &amp; raise payouts
              </button>
            </div>

            {current.items.length === 0 ? (
              <div className="pro-card pro-empty">
                <p>No payouts have been raised yet.</p>
              </div>
            ) : (
              <div className="pro-table-scroll">
                <table className="pro-table">
                  <thead>
                    <tr>
                      <th>Creator</th>
                      <th>Month</th>
                      <th>Amount</th>
                      <th>Bank</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {current.items.map((p) => (
                      <tr key={p._id}>
                        <td>
                          <span className="pro-table-strong">{p.creator?.name || "—"}</span>
                          <small className="pro-table-sub">{p.creator?.email || ""}</small>
                        </td>
                        <td>{formatMonth(p.monthKey)}</td>
                        <td className="pro-table-strong">{formatMoney(p.amountRupees)}</td>
                        <td>
                          {p.bankVerified ? (
                            <>
                              <span className="apro-flag apro-flag-yes">Verified</span>
                              <small className="pro-table-sub">
                                {p.payTo?.bankName || p.bankSnapshot?.bankName} ••••
                                {p.payTo?.accountLast4 || p.bankSnapshot?.accountLast4}
                              </small>
                              {/* The full account number, on request only —
                                  this is what the transfer is made to. */}
                              <button
                                type="button"
                                className="pro-btn pro-btn-ghost pro-btn-sm"
                                style={{ marginTop: 6 }}
                                onClick={() => showPayoutBank(p)}
                              >
                                🏦 Pay to…
                              </button>
                            </>
                          ) : (
                            <span className="apro-flag apro-flag-no">Not verified</span>
                          )}
                        </td>
                        <td>
                          <span className={`pro-status-pill pro-status-${p.status}`}>
                            {STATUS_LABEL[p.status] || p.status}
                          </span>
                          {p.adminNote && <small className="pro-table-sub">{p.adminNote}</small>}
                        </td>
                        <td>
                          <select
                            className="apro-select"
                            value={p.status}
                            disabled={busy}
                            onChange={(e) => setPayoutStatus(p, e.target.value)}
                          >
                            {PAYOUT_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {STATUS_LABEL[s]}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ── 5. TRANSACTIONS  (+ Step 25 refund) ── */}
        {!loading && !error && tab === "transactions" && current && (
          <section>
            <div className="pro-table-scroll">
              <table className="pro-table">
                <thead>
                  <tr>
                    <th>Payment</th>
                    <th>Reader</th>
                    <th>Creator</th>
                    <th>Gross</th>
                    <th>Commission</th>
                    <th>Creator net</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {current.items.map((row) => (
                    <tr key={row._id} className={row.status === "refunded" ? "pro-row-void" : ""}>
                      <td>
                        <code className="apro-code">{row.paymentId}</code>
                      </td>
                      <td>{row.user?.name || "—"}</td>
                      <td>{row.creator?.name || "—"}</td>
                      <td>{formatMoney(row.grossRupees)}</td>
                      <td>{formatMoney(row.commissionRupees)}</td>
                      <td className="pro-table-strong">{formatMoney(row.netRupees)}</td>
                      <td>{formatDate(row.createdAt)}</td>
                      <td>
                        <span className={`pro-status-pill pro-status-${row.status}`}>
                          {row.status}
                        </span>
                        {row.refundReason && (
                          <small className="pro-table-sub">{row.refundReason}</small>
                        )}
                      </td>
                      <td>
                        {row.status === "refunded" ? (
                          <span className="pro-table-sub">—</span>
                        ) : (
                          <button
                            type="button"
                            className="pro-btn pro-btn-ghost pro-btn-sm"
                            onClick={() => refund(row)}
                            disabled={busy}
                          >
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate("/admin")}
        >
          ← Admin Dashboard
        </button>
      </div>
    </div>
  );
}

export default AdminPro;
